import React, { useEffect } from 'react';

interface TimePickerProps {
  value: number | null;
  onChange: (seconds: number) => void;
  min?: number;
  max?: number;
  initialValue?: number;
}

const selectStyle: React.CSSProperties = {
  appearance: 'none',
  WebkitAppearance: 'none',
  width: 80,
  height: 64,
  textAlign: 'center',
  textAlignLast: 'center',
  border: '1px solid rgba(250,253,251,0.16)',
  borderRadius: 16,
  background: 'rgba(250,253,251,0.08)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  color: '#FAFDFB',
  fontFamily: "'Figtree', sans-serif",
  fontSize: 24,
  fontWeight: 400,
  outline: 'none',
  cursor: 'pointer',
  padding: '0 8px',
  backgroundImage: 'none',
};

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function range(n: number): number[] {
  return Array.from({ length: n }, (_, i) => i);
}

const TimePicker: React.FC<TimePickerProps> = ({ value, onChange, min = 0, max = 36000, initialValue }) => {
  const total = value ?? initialValue ?? 3600;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  // Emit default value on mount so Continue is enabled immediately
  useEffect(() => {
    if (value === null || value === undefined) {
      onChange(initialValue ?? 3600);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const maxH = Math.floor(max / 3600);

  const emit = (hh: number, mm: number, ss: number) => {
    onChange(Math.max(min, Math.min(max, hh * 3600 + mm * 60 + ss)));
  };

  return (
    <>
      <style>{`
        .urunn-time-select::-ms-expand { display: none; }
        .urunn-time-select option {
          background: #1B2B48;
          color: #FAFDFB;
        }
        .urunn-time-select:focus {
          border-color: rgba(250,253,251,0.24);
          background: rgba(250,253,251,0.12);
        }
      `}</style>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {/* Hours */}
        <div style={{ textAlign: 'center' }}>
          <select
            className="urunn-time-select"
            value={h}
            onChange={(e) => emit(Number(e.target.value), m, s)}
            style={selectStyle}
          >
            {range(maxH + 1).map((v) => (
              <option key={v} value={v}>{pad(v)}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: 'rgba(250,253,251,0.4)', marginTop: 6, fontFamily: "'Figtree', sans-serif" }}>
            hours
          </div>
        </div>

        <span style={{ color: 'rgba(250,253,251,0.3)', fontSize: 28, fontFamily: "'Figtree', sans-serif", paddingBottom: 22 }}>:</span>

        {/* Minutes */}
        <div style={{ textAlign: 'center' }}>
          <select
            className="urunn-time-select"
            value={m}
            onChange={(e) => emit(h, Number(e.target.value), s)}
            style={selectStyle}
          >
            {range(60).map((v) => (
              <option key={v} value={v}>{pad(v)}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: 'rgba(250,253,251,0.4)', marginTop: 6, fontFamily: "'Figtree', sans-serif" }}>
            min
          </div>
        </div>

        <span style={{ color: 'rgba(250,253,251,0.3)', fontSize: 28, fontFamily: "'Figtree', sans-serif", paddingBottom: 22 }}>:</span>

        {/* Seconds */}
        <div style={{ textAlign: 'center' }}>
          <select
            className="urunn-time-select"
            value={s}
            onChange={(e) => emit(h, m, Number(e.target.value))}
            style={selectStyle}
          >
            {range(60).map((v) => (
              <option key={v} value={v}>{pad(v)}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: 'rgba(250,253,251,0.4)', marginTop: 6, fontFamily: "'Figtree', sans-serif" }}>
            sec
          </div>
        </div>
      </div>
    </>
  );
};

export default TimePicker;
